import React from 'react';
import * as echarts from 'echarts';

import './HomeServicePackages.less';
import i2Math from '@/utils/i2Math';
import { renderTextStr, renderFormattedInt } from '@/utils/renderUtils';

class HomeServicePackages extends React.Component {
  constructor(props) {
    super(props);
    this.chartRef = React.createRef();
    this.chartInstance = null;
    this.tooltipObserver = null;
    this.initChartTimer = null; // 延迟初始化定时器
    this.isInitializing = false; // 初始化标志，防止重复初始化
    this.lastInitKey = null; // 上次初始化的 key，用于判断是否需要重新初始化
    this.updateTimer = null; // 防抖定时器
  }

  /** 将接口中的 month（'01' / 10 等）转为 1–12 的月份序号，非法则返回 -1 */
  monthToIndex1To12 = (month) => {
    const n = parseInt(String(month), 10);
    if (!Number.isFinite(n)) return -1;
    if (n < 1 || n > 12) return -1;
    return n;
  };

  /**
   * 无 store_id 时：总额用 postpartum.total_sum；折线图为全部门店按月汇总（store_map 内 list 的 sum 相加）
   */
  buildAggregatedStoreView = (postpartum) => {
    const { store_map: storeMap, total_sum: totalSum } = postpartum || {};
    if (!storeMap || typeof storeMap !== 'object') {
      return null;
    }
    const monthly = Array.from({ length: 12 }, () => 0);
    Object.values(storeMap).forEach((store) => {
      (store.list || []).forEach((item) => {
        const m = this.monthToIndex1To12(item.month);
        if (m === -1) return;
        monthly[m - 1] = i2Math.i2Add(monthly[m - 1], Number(item.sum) || 0);
      });
    });
    const list = monthly.map((sum, i) => ({ month: i + 1, sum }));
    let total = totalSum;
    if (total == null) {
      total = list.reduce((acc, row) => i2Math.i2Add(acc, Number(row.sum) || 0), 0);
    }
    return { total, list };
  };

  /** 有 store_id：单店；无 store_id：全部门店汇总（与 RevenueSummaryCard / MaternityPackagesManagement 一致） */
  getStoreObj = () => {
    const { erpStoreAnnualStatisticsObj, queryManager } = this.props;
    const postpartum = erpStoreAnnualStatisticsObj?.postpartum;
    if (!postpartum) {
      return null;
    }

    const storeId = queryManager?.currentQuery?.store_id;
    const hasStoreId = storeId !== undefined && storeId !== null && String(storeId).trim() !== '';

    if (hasStoreId) {
      const id = String(storeId);
      return postpartum.store_map?.[id] || null;
    }

    return this.buildAggregatedStoreView(postpartum);
  };

  getChartDataSignature = (storeObj) => {
    if (!storeObj?.list?.length) return '';
    const listSig = storeObj.list.map((row) => row.sum).join(',');
    return `${storeObj.total}_${listSig}`;
  };

  // 初始化图表
  initChart = () => {
    if (!this.chartRef.current) return;

    const storeObj = this.getStoreObj();
    if (!storeObj?.list?.length) {
      return;
    }

    const { queryManager } = this.props;
    const sid = queryManager?.currentQuery?.store_id;
    const modePrefix =
      sid !== undefined && sid !== null && String(sid).trim() !== '' ? String(sid) : 'ALL';
    const initKey = `${modePrefix}_${this.getChartDataSignature(storeObj)}`;
    
    // 如果数据没有变化且图表已存在，只更新数据，不重新初始化
    if (this.chartInstance && this.lastInitKey === initKey) {
      this.updateChartData(storeObj);
      return;
    }

    // 如果正在初始化，取消之前的初始化
    if (this.isInitializing) {
      if (this.initChartTimer) {
        cancelAnimationFrame(this.initChartTimer);
        this.initChartTimer = null;
      }
      return;
    }

    // 检查容器高度，如果为 0 则延迟初始化
    const containerHeight = this.chartRef.current.offsetHeight;
    if (containerHeight === 0) {
      this.isInitializing = true;
      this.initChartTimer = requestAnimationFrame(() => {
        this.initChartTimer = null;
        this.isInitializing = false;
        if (this.chartRef.current?.offsetHeight > 0) {
          this.initChart();
        }
      });
      return;
    }

    this.isInitializing = true;
    this.lastInitKey = initKey;

    // 清理旧的 observer
    if (this.tooltipObserver) {
      this.tooltipObserver.disconnect();
      this.tooltipObserver = null;
    }

    // 如果图表已存在，先销毁
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }

    this.chartInstance = echarts.init(this.chartRef.current);

    // 计算自适应字体大小
    const axisLabelFontSize = Math.max(8, Math.min(12, containerHeight * 0.15));
    const tooltipFontSize = Math.max(10, Math.min(14, containerHeight * 0.2));

    // 提取图表数据
    const { amounts, labels } = storeObj.list.reduce((acc, item) => {
      acc.amounts.push(item.sum || 0);
      acc.labels.push(`${item.month}月`);
      return acc;
    }, { amounts: [], labels: [] });
    const option = {
      backgroundColor: 'transparent',
      animation: true,
      animationDuration: 1200,
      animationEasing: 'cubicOut',
      animationDurationUpdate: 900,
      animationEasingUpdate: 'cubicInOut',
      grid: {
        left: '3%',
        right: '3%',
        top: '5%',
        bottom: '5%',
        containLabel: true
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'line'
        },
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        borderColor: '#1680aa',
        borderWidth: 1,
        textStyle: {
          color: '#ffffff',
          fontSize: tooltipFontSize
        },
        z: 100000,
        extraCssText: 'z-index: 100000 !important; position: fixed !important;',
        appendToBody: true,
        formatter: (params) => {
          const param = params[0];
          return `${param.name}<br/>${param.seriesName}: ${renderFormattedInt(param.value)}元`;
        }
      },
      legend: {
        show: false
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: labels,
        axisLabel: {
          color: '#8c8c8c',
          fontSize: axisLabelFontSize,
          interval: 'auto',
          rotate: 0
        },
        axisLine: {
          lineStyle: {
            color: '#e8e8e8'
          }
        },
        axisTick: {
          show: false
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          color: '#8c8c8c',
          fontSize: axisLabelFontSize,
          formatter: (value) => {
            if (value >= 10000) {
              return (value / 10000).toFixed(1) + '万';
            }
            return value;
          }
        },
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        splitLine: {
          lineStyle: {
            color: '#f0f0f0',
            type: 'dashed'
          }
        }
      },
      series: [
        {
          name: '月产康',
          type: 'line',
          smooth: true,
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(22, 128, 170, 0.3)' },
                { offset: 1, color: 'rgba(22, 128, 170, 0.05)' }
              ]
            }
          },
          lineStyle: {
            color: '#1680aa',
            width: 2,
            shadowBlur: 10,
            shadowColor: 'rgba(22, 128, 170, 0.45)',
            shadowOffsetY: 0
          },
          itemStyle: {
            color: '#1680aa'
          },
          data: amounts,
          emphasis: {
            focus: 'series',
            lineStyle: {
              width: 3,
              shadowBlur: 16,
              shadowColor: 'rgba(22, 128, 170, 0.65)'
            },
            itemStyle: {
              borderWidth: 2,
              borderColor: '#fff'
            }
          }
        }
      ]
    };

    this.chartInstance.setOption(option, {
      renderer: 'canvas'
    });

    // 使用 MutationObserver 监听 tooltip 元素的创建
    this.tooltipObserver = new MutationObserver(() => {
      const tooltipElements = document.querySelectorAll('.echarts-tooltip');
      tooltipElements.forEach(el => {
        if (el instanceof HTMLElement) {
          if (el.parentElement !== document.body) {
            document.body.appendChild(el);
          }
          el.style.zIndex = '100000';
          el.style.position = 'fixed';
          el.style.pointerEvents = 'none';
        }
      });
    });

    this.tooltipObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    this.isInitializing = false;
  };

  // 更新图表数据（不重新初始化）
  updateChartData = (storeObj) => {
    if (!this.chartInstance || !storeObj?.list?.length) {
      return;
    }

    // 提取图表数据
    const { amounts, labels } = storeObj.list.reduce((acc, item) => {
      acc.amounts.push(item.sum || 0);
      acc.labels.push(`${item.month}月`);
      return acc;
    }, { amounts: [], labels: [] });

    // 只更新数据，不重新初始化
    this.chartInstance.setOption({
      xAxis: {
        data: labels
      },
      series: [{
        data: amounts
      }]
    }, { notMerge: false, lazyUpdate: false });
  };

  componentDidMount() {
    this.initChart();
    window.addEventListener('resize', this.handleResize);
  }

  componentDidUpdate(prevProps) {
    const { erpStoreAnnualStatisticsObj, queryManager } = this.props;

    const prevSid = prevProps.queryManager?.currentQuery?.store_id;
    const curSid = queryManager?.currentQuery?.store_id;
    const prevHasStore =
      prevSid !== undefined && prevSid !== null && String(prevSid).trim() !== '';
    const curHasStore =
      curSid !== undefined && curSid !== null && String(curSid).trim() !== '';

    const storeIdChanged =
      prevHasStore !== curHasStore ||
      (prevHasStore && curHasStore && String(prevSid) !== String(curSid));

    const prevPostpartum = prevProps.erpStoreAnnualStatisticsObj?.postpartum;
    const curPostpartum = erpStoreAnnualStatisticsObj?.postpartum;

    let storeDataChanged = false;
    if (!storeIdChanged) {
      if (curHasStore) {
        const prevStoreObj = prevPostpartum?.store_map?.[String(prevSid)];
        const currentStoreObj = curPostpartum?.store_map?.[String(curSid)];
        storeDataChanged = JSON.stringify(prevStoreObj) !== JSON.stringify(currentStoreObj);
      } else {
        storeDataChanged =
          prevPostpartum?.total_sum !== curPostpartum?.total_sum ||
          JSON.stringify(prevPostpartum?.store_map) !== JSON.stringify(curPostpartum?.store_map);
      }
    }

    if (storeIdChanged || storeDataChanged) {
      // 如果正在初始化，先等待完成
      if (this.isInitializing) {
        return;
      }
      
      // 清除之前的初始化标志和定时器
      if (this.initChartTimer) {
        cancelAnimationFrame(this.initChartTimer);
        this.initChartTimer = null;
      }
      this.lastInitKey = null; // 清除 key，强制重新初始化
      
      // 使用 setTimeout 防抖，避免快速连续更新
      if (this.updateTimer) {
        clearTimeout(this.updateTimer);
      }
      this.updateTimer = setTimeout(() => {
        this.updateTimer = null;
        this.initChart();
      }, 100); // 100ms 防抖
    }
  }

  componentWillUnmount() {
    // 清理定时器
    if (this.initChartTimer) {
      cancelAnimationFrame(this.initChartTimer);
      this.initChartTimer = null;
    }
    
    if (this.updateTimer) {
      clearTimeout(this.updateTimer);
      this.updateTimer = null;
    }
    
    // 清理 MutationObserver
    if (this.tooltipObserver) {
      this.tooltipObserver.disconnect();
      this.tooltipObserver = null;
    }
    
    // 销毁图表
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
    
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = () => {
    if (this.chartInstance) {
      this.chartInstance.resize();
    }
  };

  render() {
    const { homeServiceSummaryObj, queryManager, monthRevenueData, storeDataByDay } = this.props;
    
    if (!homeServiceSummaryObj?.data || !queryManager?.currentQuery) {
      return null;
    }

    const homeServiceSummaryData = homeServiceSummaryObj.data;
    const storeObj = this.getStoreObj() || {};
    // 使用父组件预计算的月份收入数据（postpartum 对应到家套餐）
    const monthRevenue = monthRevenueData?.postpartum || 0;
    // 使用父组件预计算的门店日数据
    const storeDataByDayObj = storeDataByDay || {};
    
    return (
    <div className="home-service-packages">
      {/* 顶部：日产康签单和月产康签单 */}
      <div className="home-service-section-top">
        <div className="section-half left">
          <div className="home-service-section-title">日产康签单</div>
          <div className="home-service-amount-large">
            <span className="number">{renderFormattedInt(storeDataByDayObj.health)}</span>
            <span className="unit">元</span>
          </div>
          <div className="home-service-amount-small">{renderTextStr(homeServiceSummaryData.todayHomeServiceCount)} 单</div>
        </div>
        <div className="divider"></div>
        <div className="section-half right">
          <div className="home-service-section-title">月产康签单</div>
          <div className="home-service-amount-large">
            <span className="number">{renderFormattedInt(monthRevenue)}</span>
            <span className="unit">元</span>
          </div>
          <div className="home-service-amount-small">{renderTextStr(homeServiceSummaryData.monthHomeServiceCount)} 单</div>
        </div>
      </div>

      {/* 中间：年度产康签单金额 */}
      <div className="home-service-section-middle">
        <div className="home-service-section-header">
          <div className="home-service-section-title-wrapper">
            <div className="home-service-section-title-dark">年度产康签单金额</div>
          </div>
        </div>
        <div className="home-service-chart-container">
          <div ref={this.chartRef} className="home-service-chart-echarts"></div>
        </div>
        <div className="home-service-metrics">
          <div className="home-service-metric-primary">
            <span className="number">{renderFormattedInt(storeObj.total)}</span>
            <span className="unit">元</span>
          </div>
        </div>
      </div>
    </div>
    );
  }
}

export default HomeServicePackages;
